import React, { useEffect, useMemo, useRef, useState } from "react";
import { ClickAwayListener, Stack, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";

import { ISearchItem, SearchItemType } from "store/types";
import { SearchPopover } from "./Search-popper";
import { useDealActions } from "store";
import { useActionHook } from "hooks";

export interface ISearchResults {
  all: ISearchItem[];
  deals: ISearchItem[];
  files: ISearchItem[];
  workspace: ISearchItem[];
  characteristics: ISearchItem[];
}
const initialState: ISearchResults = {
  all: [],
  deals: [],
  files: [],
  workspace: [],
  characteristics: []
};

export const AdvancedSearch = () => {
  const searchRef = useRef<HTMLDivElement>(null);
  const [popperOpen, setPopperOpen] = useState(false);
  const [state, setState] = useState<ISearchResults>(initialState);
  const [searchValue, setSearchValue] = useState<string>("");

  const { advancedSearchAction } = useDealActions();
  const { action, resetData, loading } = useActionHook(advancedSearchAction, {
    onSuccess: (data) => {
      const sortedResults: ISearchResults = {
        all: data,
        deals: [],
        files: [],
        workspace: [],
        characteristics: []
      };

      data.forEach((item: ISearchItem) => {
        if (item.type === SearchItemType.Deal) {
          sortedResults.deals.push(item);
        } else if (item.type === SearchItemType.File) {
          sortedResults.files.push(item);
        } else if (
          item.type === SearchItemType.Form ||
          item.type === SearchItemType.Document ||
          item.type === SearchItemType.Folder
        ) {
          sortedResults.workspace.push(item);
        } else if (item.type === SearchItemType.Characteristic) {
          sortedResults.characteristics.push(item);
        }
      });

      setState(sortedResults);
      setPopperOpen(true);
    }
  });

  const debounceUpdate = useMemo(() => {
    return debounce(action, 600);
  }, []);

  useEffect(() => {
    if (!searchValue.length) {
      resetData();
      return debounceUpdate.cancel();
    }
    debounceUpdate(searchValue);
  }, [searchValue]);

  const resetSearchState = () => {
    setSearchValue("");
    setState(initialState);
    setPopperOpen(false);
  };

  const onClose = () => {
    setPopperOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Stack sx={{ maxWidth: "500px", minWidth: "250px", flexGrow: 1 }}>
        <Stack ref={searchRef}>
          <TextField
            value={searchValue}
            onFocus={() => state.all.length && setPopperOpen(true)}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                height: "30px",
                "& fieldset": {
                  border: "1px solid #e2e2e1"
                },
                "&:hover fieldset": {
                  border: "1px solid #e2e2e1"
                },
                "&.Mui-focused fieldset": {
                  border: "1px solid #e2e2e1",
                  boxShadow: "none"
                }
              }
            }}
            placeholder="Search ..."
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ marginRight: "10px" }} />
              }
            }}
          />
        </Stack>
        <SearchPopover
          open={popperOpen}
          anchorEl={searchRef.current}
          loading={loading}
          state={state}
          searchValue={searchValue}
          onClose={onClose}
          resetSearchState={resetSearchState}
          inputWidth={searchRef.current?.offsetWidth || 500}
        />
      </Stack>
    </ClickAwayListener>
  );
};
